// Dynamic Brand Intelligence Report PDF Template
// Generates from REAL platform data — single source of truth
// Full i18n: ALL labels, dimensions, roles, levels translated per language

/* ═══ DATA-LAYER I18N — translates strings from intelligence/profile data ═══ */
const DATA_I18N = {
  es: {
    // Roles
    'Assistant Coach': 'Entrenador asistente',
    'Head Coach': 'Entrenador principal',
    'Supporting Staff': 'Staff técnico',
    'Executive Leadership': 'Liderazgo ejecutivo',

    // Brand dimensions (from coachingIntelligenceData.js)
    'Leadership Reputation Index': 'Índice de reputación de liderazgo',
    'Historical Performance Prestige': 'Prestigio de rendimiento histórico',
    'Media Authority Score': 'Autoridad mediática',
    'Digital Presence Strength': 'Presencia digital',
    'Global Recognition Index': 'Reconocimiento global',
    'Institutional Alignment Score': 'Alineación institucional',

    // Dimension descriptions
    'Historical leadership track record and industry standing': 'Trayectoria histórica de liderazgo y posición en la industria',
    'Career trophy count, win rates, and milestone achievements': 'Palmarés, tasas de victoria y logros profesionales',
    'Press conference impact, quote circulation, narrative control': 'Impacto en conferencias de prensa, circulación de citas y control narrativo',
    'Social media footprint and engagement metrics': 'Huella en redes sociales y métricas de engagement',
    'International brand awareness and market penetration': 'Reconocimiento internacional de marca y penetración de mercado',
    'Federation relationship strength and strategic fit': 'Fortaleza de la relación con la federación y encaje estratégico',

    // Benchmark positions
    'Top 15% globally': 'Top 15% a nivel global',
    'Top 45% among assistants': 'Top 45% entre entrenadores asistentes',

    // Risk levels
    'Low': 'Bajo',
    'Low-Moderate': 'Bajo-Moderado',
    'Moderate': 'Moderado',
    'Elevated': 'Elevado',
    'High': 'Alto',
    'Very Low': 'Muy bajo',

    // Search trends
    'Stable': 'Estable',
    'Rising': 'En ascenso',
    'Declining': 'En descenso',

    // Commercial categories
    'Coaching Equipment': 'Equipamiento deportivo',
    'Training Tech': 'Tecnología de entrenamiento',
    'Sportswear': 'Ropa deportiva',
    'Watches/Luxury': 'Relojes / Lujo',
    'Financial Services': 'Servicios financieros',
    'Automotive': 'Automoción',

    // Sentiment
    'positive': 'positivo',
    'neutral': 'neutro',
    'negative': 'negativo',

    // Misc
    'views/mo': 'vistas/mes',
    'total reach': 'alcance total',
    'market rate': 'tarifa mercado',
    'annual potential': 'potencial anual',
  },
};

function t(text, lang) {
  if (lang === 'en' || !text) return text;
  return DATA_I18N[lang]?.[text] || text;
}

/* ═══ STATIC LABELS — section titles, field labels, etc. ═══ */
const LABELS = {
  en: {
    title: 'BRAND INTELLIGENCE REPORT',
    subtitle: 'Strategic Profile in Professional Soccer',
    tagline: 'Brand Score · Reputation Impact · Commercial Potential · Opportunities',
    fileName: 'Brand_Intelligence_Report_Jesus_Perez_YBV_Sports.pdf',
    confidential: 'CONFIDENTIAL — Internal use',
    metaLabels: { type: 'Report type', date: 'Issue date', version: 'Version', methodology: 'Methodology', generated: 'Generated by' },
    metaValues: { type: 'Individual Brand Intelligence', version: 'Strategic profile', methodology: 'ISO 10668 + YBV Sports algorithm', generated: 'Your Brand Value Sports' },
    profileTitle: 'Profile',
    profileLabels: { name: 'Name', sector: 'Sector', role: 'Role', spec: 'Specialization', market: 'Market', asset: 'Analysed asset' },
    profileValues: { sector: 'Professional soccer', spec: 'High performance, sports leadership, methodology and team management', market: 'International', asset: 'Professional personal brand linked to elite soccer' },
    execTitle: 'Executive Summary',
    brandScoreTitle: 'Brand Score',
    dimensionsTitle: 'Brand Dimensions',
    driversTitle: 'Brand Drivers Analysis',
    driversUp: 'Top Growth Drivers',
    driversDown: 'Limiting Factors',
    digitalTitle: 'Digital Presence Analysis',
    platform: 'Platform', followers: 'Followers', engagement: 'Engagement', growth: 'Growth',
    dominantPlatform: 'Platform dominance',
    mentions30d: 'Media mentions (30 days)',
    sentiment: 'Sentiment',
    commercialTitle: 'Commercial Potential',
    commercialLevel: 'Potential Level',
    estimatedBrandValue: 'Estimated Brand Value',
    category: 'Category',
    estimatedRange: 'Estimated Range',
    readiness: 'Readiness',
    speaking: 'Speaking',
    marketRate: 'market rate',
    annualPotential: 'annual potential',
    riskTitle: 'Risk Analysis',
    benchmarkTitle: 'Benchmarking',
    benchProfile: 'Profile',
    position: 'Position',
    recsTitle: 'Strategic Recommendations',
    methodTitle: 'Methodological Note',
    methodText: 'The valuation and brand analysis contained in this report are based on Your Brand Value Sports\' proprietary methodology, aligned with international intangible asset valuation standards (ISO 10668). The model integrates reputation signals, public data, digital presence metrics, professional trajectory indicators and sector comparative models.',
    disclaimer: 'This report is for strategic and directional purposes only. It does not constitute an official financial valuation or economic expert report. The assessment is based on public signals, Your Brand Value Sports\' proprietary methodology and brand intelligence models aligned with international intangible asset valuation standards.',
    copyright: '\u00a9 2026 Your Brand Value Sports. All rights reserved.',
    directional: 'Directional',
    high: 'High', moderate: 'Moderate', low: 'Low', strong: 'Strong',
    aboveBenchmark: 'Above benchmark', alignedBenchmark: 'Aligned with benchmark', belowBenchmark: 'Below benchmark',
  },
  es: {
    title: 'INFORME DE INTELIGENCIA DE MARCA',
    subtitle: 'Perfil estrat\u00e9gico en f\u00fatbol profesional',
    tagline: 'Brand Score \u00b7 Impacto reputacional \u00b7 Potencial comercial \u00b7 Oportunidades',
    fileName: 'Informe_Brand_Intelligence_Jesus_Perez_YBV_Sports.pdf',
    confidential: 'CONFIDENCIAL \u2014 Uso interno',
    metaLabels: { type: 'Tipo de informe', date: 'Fecha de emisi\u00f3n', version: 'Versi\u00f3n', methodology: 'Metodolog\u00eda', generated: 'Generado por' },
    metaValues: { type: 'Brand Intelligence Individual', version: 'Perfil estrat\u00e9gico', methodology: 'ISO 10668 + algoritmo YBV Sports', generated: 'Your Brand Value Sports' },
    profileTitle: 'Perfil',
    profileLabels: { name: 'Nombre', sector: 'Sector', role: 'Rol', spec: 'Especializaci\u00f3n', market: 'Mercado', asset: 'Activo analizado' },
    profileValues: { sector: 'F\u00fatbol profesional', spec: 'Alto rendimiento, liderazgo deportivo, metodolog\u00eda y gesti\u00f3n de equipos', market: 'Internacional', asset: 'Marca personal profesional vinculada al f\u00fatbol de \u00e9lite' },
    execTitle: 'Resumen Ejecutivo',
    brandScoreTitle: 'Brand Score',
    dimensionsTitle: 'Dimensiones de marca',
    driversTitle: 'An\u00e1lisis de Drivers de Marca',
    driversUp: 'Principales Impulsores',
    driversDown: 'Factores Limitantes',
    digitalTitle: 'An\u00e1lisis de Presencia Digital',
    platform: 'Plataforma', followers: 'Seguidores', engagement: 'Engagement', growth: 'Crecimiento',
    dominantPlatform: 'Plataforma dominante',
    mentions30d: 'Menciones en medios (30 d\u00edas)',
    sentiment: 'Sentimiento',
    commercialTitle: 'Potencial Comercial',
    commercialLevel: 'Nivel de Potencial',
    estimatedBrandValue: 'Valor de marca estimado',
    category: 'Categor\u00eda',
    estimatedRange: 'Rango estimado',
    readiness: 'Preparaci\u00f3n',
    speaking: 'Conferencias',
    marketRate: 'tarifa mercado',
    annualPotential: 'potencial anual',
    riskTitle: 'An\u00e1lisis de Riesgos',
    benchmarkTitle: 'Benchmarking',
    benchProfile: 'Perfil',
    position: 'Posici\u00f3n',
    recsTitle: 'Recomendaciones Estrat\u00e9gicas',
    methodTitle: 'Nota Metodol\u00f3gica',
    methodText: 'La valoraci\u00f3n y el an\u00e1lisis de marca contenidos en este informe se basan en la metodolog\u00eda propietaria de Your Brand Value Sports, alineada con est\u00e1ndares internacionales de valoraci\u00f3n de activos intangibles (ISO 10668). El modelo integra se\u00f1ales de reputaci\u00f3n, datos p\u00fablicos, m\u00e9tricas de presencia digital, indicadores de trayectoria profesional y modelos comparativos del sector.',
    disclaimer: 'Este informe tiene car\u00e1cter orientativo y estrat\u00e9gico. No constituye una valoraci\u00f3n financiera oficial ni peritaje econ\u00f3mico. La valoraci\u00f3n se basa en se\u00f1ales p\u00fablicas, metodolog\u00eda propia de Your Brand Value Sports y modelos de inteligencia de marca alineados con est\u00e1ndares internacionales de valoraci\u00f3n de activos intangibles.',
    copyright: '\u00a9 2026 Your Brand Value Sports. Todos los derechos reservados.',
    directional: 'Direccional',
    high: 'Alto', moderate: 'Moderado', low: 'Bajo', strong: 'Fuerte',
    aboveBenchmark: 'Por encima del benchmark', alignedBenchmark: 'Alineado con el benchmark', belowBenchmark: 'Por debajo del benchmark',
  },
};

/* ═══ Helpers ═══ */
function fmtNum(n) { if (n >= 1000000) return `${(n/1000000).toFixed(1)}M`; if (n >= 1000) return `${(n/1000).toFixed(1)}K`; return String(n); }
function fmtCurrency(v) { if (v >= 1000000) return `$${(v/1000000).toFixed(1)}M`; if (v >= 1000) return `$${(v/1000).toFixed(0)}K`; return `$${v}`; }
function scoreLevel(s, l) { if (s >= 80) return l.strong; if (s >= 60) return l.high; if (s >= 40) return l.moderate; return l.low; }
function barHTML(score, maxW) {
  const c = score >= 80 ? '#10b981' : score >= 60 ? '#3b82f6' : score >= 40 ? '#f59e0b' : '#ef4444';
  return `<div style="display:flex;align-items:center;gap:10px;"><div style="flex:1;max-width:${maxW || 200}px;height:7px;background:#e5e7eb;border-radius:4px;overflow:hidden;"><div style="width:${score}%;height:100%;background:${c};border-radius:4px;"></div></div><span style="font-weight:700;font-size:13px;color:${c};min-width:38px;text-align:right;">${score}</span></div>`;
}

/* ═══ Dynamic content generators — all use t() for data-layer strings ═══ */

function buildExecutiveSummary(profile, intel, l, lang) {
  const bs = intel.brandScoreDecomposition;
  const strongest = [...bs.factors].sort((a,b) => b.rawScore - a.rawScore)[0];
  const weakest  = [...bs.factors].sort((a,b) => a.rawScore - b.rawScore)[0];
  const score = bs.overallScore || bs.calculatedTotal?.toFixed(0);

  if (lang === 'es') {
    return `<p>Este informe analiza el valor estrat\u00e9gico de la marca profesional de ${profile.name} dentro del ecosistema del f\u00fatbol de \u00e9lite, integrando se\u00f1ales de reputaci\u00f3n, trayectoria, presencia digital, autoridad sectorial y potencial comercial.</p>
    <p style="margin-top:12px;">Con un Brand Score de <strong>${score}/100</strong>, su dimensi\u00f3n m\u00e1s fuerte es <strong>${t(strongest.name, lang)}</strong> (${strongest.rawScore}/100), mientras que el principal gap detectado est\u00e1 en <strong>${t(weakest.name, lang)}</strong> (${weakest.rawScore}/100). La oportunidad principal reside en convertir su autoridad profesional en productos escalables como advisory, formaci\u00f3n y conferencias.</p>`;
  }
  return `<p>This report analyses the strategic value of ${profile.name}'s professional brand within the elite soccer ecosystem, integrating reputation signals, career trajectory, digital presence, sector authority and commercial potential.</p>
  <p style="margin-top:12px;">With a Brand Score of <strong>${score}/100</strong>, the strongest dimension is <strong>${strongest.name}</strong> (${strongest.rawScore}/100), while the main gap detected is in <strong>${weakest.name}</strong> (${weakest.rawScore}/100). The primary opportunity lies in translating professional authority into scalable products such as advisory, education and speaking engagements.</p>`;
}

function buildDrivers(intel, l, lang) {
  const factors = [...intel.brandScoreDecomposition.factors].sort((a,b) => b.rawScore - a.rawScore);
  const top3 = factors.slice(0, 3);
  const bottom3 = factors.slice(-3).reverse();

  const upItems = top3.map(f => `<div class="finding"><div class="finding-num" style="background:#10b981;">+</div><div class="finding-text"><strong>${t(f.name, lang)}</strong> (${f.rawScore}/100) \u2014 ${t(f.description, lang)}</div></div>`).join('');
  const downItems = bottom3.map(f => `<div class="finding"><div class="finding-num" style="background:#f59e0b;">!</div><div class="finding-text"><strong>${t(f.name, lang)}</strong> (${f.rawScore}/100) \u2014 ${t(f.description, lang)}</div></div>`).join('');

  return `<h3 style="font-size:14px;font-weight:600;color:#10b981;margin-bottom:12px;">${l.driversUp}</h3>${upItems}
  <h3 style="font-size:14px;font-weight:600;color:#f59e0b;margin:20px 0 12px;">${l.driversDown}</h3>${downItems}`;
}

function buildDigital(intel, l, lang) {
  const social = intel.digitalMediaIntelligence.socialPresence;
  const rows = [];
  if (social.instagram?.followers) rows.push({ platform: 'Instagram', followers: fmtNum(social.instagram.followers), engagement: `${social.instagram.engagement}%`, growth: `+${social.instagram.monthlyGrowth}%/mo` });
  if (social.twitter?.followers) rows.push({ platform: 'X / Twitter', followers: fmtNum(social.twitter.followers), engagement: `${social.twitter.engagement}%`, growth: `+${social.twitter.monthlyGrowth}%/mo` });
  if (social.linkedin?.connections) rows.push({ platform: 'LinkedIn', followers: fmtNum(social.linkedin.connections), engagement: `${social.linkedin.profileViews} ${t('views/mo', lang)}`, growth: '\u2014' });

  const media = intel.digitalMediaIntelligence.mediaMetrics;
  const search = intel.digitalMediaIntelligence.searchAnalytics;
  const totalFollowers = (social.instagram?.followers || 0) + (social.twitter?.followers || 0);
  const dominantPlatform = (social.instagram?.followers || 0) >= (social.twitter?.followers || 0) ? 'Instagram' : 'X / Twitter';

  let tableRows = rows.map(r => `<tr><td style="font-weight:500;">${r.platform}</td><td>${r.followers}</td><td>${r.engagement}</td><td>${r.growth}</td></tr>`).join('');

  return `<table class="data-table"><thead><tr><th>${l.platform}</th><th>${l.followers}</th><th>${l.engagement}</th><th>${l.growth}</th></tr></thead><tbody>${tableRows}</tbody></table>
  <div style="margin-top:16px;font-size:12px;color:#475569;line-height:1.7;">
    <p><strong>${l.dominantPlatform}:</strong> ${dominantPlatform} (${fmtNum(totalFollowers)} ${t('total reach', lang)})</p>
    <p><strong>${l.mentions30d}:</strong> ${media.mentionsLast30Days} (${media.mentionsTrend})</p>
    <p><strong>${l.sentiment}:</strong> ${media.sentimentScore}/100 \u2014 ${media.sentimentBreakdown.positive}% ${t('positive', lang)}, ${media.sentimentBreakdown.neutral}% ${t('neutral', lang)}, ${media.sentimentBreakdown.negative}% ${t('negative', lang)}</p>
    <p><strong>Google Trends:</strong> ${search.googleTrendsIndex}/100 (${t(search.searchVolumeTrend, lang)})</p>
  </div>`;
}

function buildCommercial(intel, profile, l, lang) {
  const sp = intel.commercialPositioning.sponsorshipLeverage;
  const speak = intel.commercialPositioning.speakingEngagements;
  const val = intel.executiveSummary.valueRange;
  const level = sp.influenceScore >= 60 ? l.high : sp.influenceScore >= 35 ? l.moderate : l.low;

  const categories = sp.potentialCategories.map(c => `<tr><td>${t(c.category, lang)}</td><td style="font-weight:600;">${c.potential}</td><td>${barHTML(c.readiness, 120)}</td></tr>`).join('');

  return `<div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:20px;margin-bottom:16px;">
    <p style="font-size:12px;color:#64748b;text-transform:uppercase;letter-spacing:1px;margin-bottom:4px;">${l.estimatedBrandValue}</p>
    <p style="font-size:28px;font-weight:800;color:#3b82f6;">${fmtCurrency(val.min)} \u2013 ${fmtCurrency(val.max)}</p>
    <p style="font-size:12px;color:#94a3b8;margin-top:4px;">${l.directional}</p>
  </div>
  <p style="margin-bottom:12px;"><strong>${l.commercialLevel}:</strong> ${level} (${sp.influenceScore}/100)</p>
  <table class="data-table"><thead><tr><th>${l.category}</th><th>${l.estimatedRange}</th><th>${l.readiness}</th></tr></thead><tbody>${categories}</tbody></table>
  <div style="margin-top:16px;font-size:12px;color:#475569;">
    <p><strong>${l.speaking}:</strong> ${speak.marketRate} (${l.marketRate}) \u00b7 ${speak.annualPotential} (${l.annualPotential})</p>
  </div>`;
}

function buildRisks(intel, l, lang) {
  const risk = intel.riskVolatility;
  const items = [
    { label: lang === 'es' ? 'Sensibilidad a controversia' : 'Controversy sensitivity', score: risk.mediaControversySensitivity.score, level: t(risk.mediaControversySensitivity.level, lang) },
    { label: lang === 'es' ? 'Fatiga de liderazgo' : 'Leadership fatigue', score: risk.leadershipFatigueRisk.score, level: t(risk.leadershipFatigueRisk.level, lang) },
    { label: lang === 'es' ? 'Dependencia de rendimiento' : 'Performance dependency', score: risk.performanceDependencyRisk.score, level: t(risk.performanceDependencyRisk.level, lang) },
    { label: lang === 'es' ? 'Fluctuaci\u00f3n de opini\u00f3n p\u00fablica' : 'Public opinion fluctuation', score: Math.round(risk.publicOpinionFluctuation.index * 100), level: risk.publicOpinionFluctuation.volatilityBand },
  ];
  return items.map(r => `<div class="dim-row"><div class="dim-label">${r.label}</div><div style="flex:1;">${barHTML(r.score, 180)}</div><div style="font-size:11px;color:#64748b;min-width:70px;text-align:right;">${r.level}</div></div>`).join('');
}

function buildBenchmarking(intel, l, lang) {
  const bench = intel.benchmarking;
  const rows = bench.concacafComparison.coaches.filter(c => c.score).map(c => {
    const label = c.name === 'Supporting Staff' ? t('Supporting Staff', lang) : c.name;
    return `<tr><td style="font-weight:500;">${label}</td><td style="text-align:center;">${c.score}/100</td></tr>`;
  }).join('');
  const pos = bench.concacafComparison.leadershipRankPercentile >= 70 ? l.aboveBenchmark : bench.concacafComparison.leadershipRankPercentile >= 40 ? l.alignedBenchmark : l.belowBenchmark;
  return `<table class="data-table"><thead><tr><th>${l.benchProfile}</th><th style="text-align:center;">Score</th></tr></thead><tbody>${rows}</tbody></table>
  <p style="margin-top:12px;font-size:12px;color:#475569;"><strong>${l.position}:</strong> ${pos} \u2014 Percentile: ${bench.concacafComparison.leadershipRankPercentile}%</p>`;
}

function buildRecommendations(intel, profile, l, lang) {
  const bs = intel.brandScoreDecomposition;
  const weakest = [...bs.factors].sort((a,b) => a.rawScore - b.rawScore).slice(0, 2);
  const commercial = intel.commercialPositioning;
  const digital = intel.digitalMediaIntelligence;
  const weakName = t(weakest[0].name, lang);
  const growthPlatform = (digital.socialPresence.instagram?.followers || 0) < (digital.socialPresence.twitter?.followers || 0) ? 'Instagram' : 'LinkedIn';

  const recs = lang === 'es' ? [
    `Fortalecer <strong>${weakName}</strong> (actualmente ${weakest[0].rawScore}/100) mediante una estrategia de posicionamiento p\u00fablico m\u00e1s estructurada.`,
    `Desarrollar una l\u00ednea de contenidos propios orientada a entrenadores, clubes y federaciones, capitalizando la alta credibilidad t\u00e9cnica.`,
    `Explorar formatos de advisory premium (${commercial.speakingEngagements.marketRate}) para clubes, academias o proyectos deportivos.`,
    `Activar una estrategia de presencia digital m\u00e1s consistente \u2014 especialmente en ${growthPlatform} donde existe mayor potencial de crecimiento.`,
    `Dise\u00f1ar un keynote profesional sobre toma de decisiones y alto rendimiento para amplificar la visibilidad internacional.`,
  ] : [
    `Strengthen <strong>${weakest[0].name}</strong> (currently ${weakest[0].rawScore}/100) through a more structured public positioning strategy.`,
    `Develop a proprietary content line aimed at coaches, clubs and federations, capitalising on high technical credibility.`,
    `Explore premium advisory formats (${commercial.speakingEngagements.marketRate}) for clubs, academies or sports projects.`,
    `Activate a more consistent digital presence strategy \u2014 especially on ${growthPlatform} where there is greater growth potential.`,
    `Design a professional keynote on decision-making and high performance to amplify international visibility.`,
  ];

  return recs.map((r, i) => `<div class="rec-item"><div class="rec-bullet">${i+1}</div><div class="rec-text">${r}</div></div>`).join('');
}

/* ═══ Main generator ═══ */
export function generateBrandIntelligenceReportPDF(lang = 'en', { profile, intelligenceData } = {}) {
  const l = LABELS[lang] || LABELS.en;
  const intel = intelligenceData;
  const bs = intel.brandScoreDecomposition;
  const overallScore = bs.overallScore || Math.round(bs.calculatedTotal);
  const now = new Date();
  const dateStr = lang === 'es'
    ? now.toLocaleDateString('es-ES', { year: 'numeric', month: 'long' })
    : now.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });

  const dimensionsHTML = bs.factors.map(f =>
    `<div class="dim-row"><div class="dim-label">${t(f.name, lang)}</div><div style="flex:1;">${barHTML(f.rawScore, 220)}</div></div>`
  ).join('');

  return `<!DOCTYPE html>
<html lang="${lang}">
<head>
<meta charset="UTF-8">
<title>${l.title} \u2014 ${profile.name}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
  *{margin:0;padding:0;box-sizing:border-box}
  body{font-family:'Inter',sans-serif;color:#0f172a;background:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact}
  @page{size:A4;margin:18mm 16mm 20mm 16mm}
  @media print{.page-break{page-break-before:always}}
  .watermark{position:fixed;top:50%;left:50%;transform:translate(-50%,-50%) rotate(-35deg);font-size:68px;font-weight:800;color:rgba(59,130,246,0.025);pointer-events:none;z-index:0;letter-spacing:6px;white-space:nowrap}
  .header-bar{background:#0f172a;color:#fff;padding:12px 28px;display:flex;justify-content:space-between;align-items:center}
  .header-bar .logo{font-weight:700;font-size:12px;letter-spacing:1px}
  .header-bar .conf{font-size:9px;color:rgba(255,255,255,0.45);letter-spacing:2px;text-transform:uppercase}
  .cover{display:flex;flex-direction:column;justify-content:center;align-items:center;min-height:82vh;text-align:center;padding:50px 36px}
  .cover-badge{display:inline-block;font-size:9px;font-weight:600;letter-spacing:3px;text-transform:uppercase;color:#3b82f6;background:#eff6ff;padding:5px 16px;border-radius:16px;margin-bottom:28px}
  .cover h1{font-size:32px;font-weight:800;letter-spacing:-0.5px;margin-bottom:6px;color:#0f172a}
  .cover .name{font-size:26px;font-weight:700;color:#3b82f6;margin-bottom:10px}
  .cover .sub{font-size:14px;color:#64748b;margin-bottom:6px}
  .cover .tagline{font-size:11px;color:#94a3b8;letter-spacing:1px;margin-top:14px}
  .meta-table{width:100%;border-collapse:collapse;margin:14px 0 24px}
  .meta-table td{padding:7px 12px;font-size:11px;border-bottom:1px solid #f1f5f9}
  .meta-table td:first-child{font-weight:600;color:#64748b;width:160px;text-transform:uppercase;letter-spacing:0.5px}
  .meta-table td:last-child{color:#0f172a}
  .section{margin-bottom:30px}
  .section-title{font-size:16px;font-weight:700;color:#0f172a;margin-bottom:14px;padding-bottom:6px;border-bottom:2px solid #3b82f6;display:inline-block}
  .score-hero{text-align:center;background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;padding:28px;margin-bottom:22px}
  .score-hero .number{font-size:56px;font-weight:800;color:#3b82f6;line-height:1}
  .score-hero .level{font-size:12px;color:#64748b;margin-top:5px}
  .dim-row{display:flex;align-items:center;gap:10px;margin-bottom:10px}
  .dim-label{width:200px;font-size:12px;font-weight:500;color:#475569;flex-shrink:0}
  .finding{display:flex;gap:9px;margin-bottom:12px}
  .finding-num{width:22px;height:22px;border-radius:50%;background:#3b82f6;color:#fff;font-size:10px;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0;margin-top:2px}
  .finding-text{font-size:12px;line-height:1.6;color:#334155}
  .rec-item{display:flex;gap:10px;margin-bottom:11px}
  .rec-bullet{width:20px;height:20px;border-radius:50%;background:#3b82f6;color:#fff;font-size:10px;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0;margin-top:2px}
  .rec-text{font-size:12px;line-height:1.6;color:#334155}
  .data-table{width:100%;border-collapse:collapse;margin:8px 0}
  .data-table th{text-align:left;font-size:10px;font-weight:600;color:#64748b;text-transform:uppercase;letter-spacing:0.5px;padding:6px 10px;border-bottom:2px solid #e2e8f0}
  .data-table td{font-size:12px;padding:8px 10px;border-bottom:1px solid #f1f5f9;color:#334155}
  .methodology-box{background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:18px;font-size:11px;color:#64748b;line-height:1.7}
  .disclaimer-box{background:#fffbeb;border:1px solid #fde68a;border-radius:8px;padding:14px 18px;font-size:10px;color:#92400e;line-height:1.6}
  .footer{text-align:center;padding:20px 0;border-top:1px solid #e2e8f0;margin-top:36px}
  .footer p{font-size:9px;color:#94a3b8;margin-bottom:3px}
</style>
</head>
<body>
<div class="watermark">${l.confidential}</div>
<div class="header-bar"><div class="logo">YOUR BRAND VALUE SPORTS</div><div class="conf">${l.confidential}</div></div>

<!-- COVER -->
<div class="cover">
  <div class="cover-badge">Brand Intelligence</div>
  <h1>${l.title}</h1>
  <div class="name">${profile.name}</div>
  <div class="sub">${l.subtitle}</div>
  <div class="tagline">${l.tagline}</div>
  <table class="meta-table" style="max-width:380px;margin:32px auto 0;">
    <tr><td>${l.metaLabels.type}</td><td>${l.metaValues.type}</td></tr>
    <tr><td>${l.metaLabels.date}</td><td>${dateStr}</td></tr>
    <tr><td>${l.metaLabels.version}</td><td>${l.metaValues.version}</td></tr>
    <tr><td>${l.metaLabels.methodology}</td><td>${l.metaValues.methodology}</td></tr>
    <tr><td>${l.metaLabels.generated}</td><td>${l.metaValues.generated}</td></tr>
  </table>
</div>

<div class="page-break"></div>

<!-- PROFILE -->
<div class="section">
  <div class="section-title">${l.profileTitle}</div>
  <table class="meta-table">
    <tr><td>${l.profileLabels.name}</td><td>${profile.name}</td></tr>
    <tr><td>${l.profileLabels.sector}</td><td>${l.profileValues.sector}</td></tr>
    <tr><td>${l.profileLabels.role}</td><td>${t(profile.role, lang)}</td></tr>
    <tr><td>${l.profileLabels.spec}</td><td>${l.profileValues.spec}</td></tr>
    <tr><td>${l.profileLabels.market}</td><td>${l.profileValues.market}</td></tr>
    <tr><td>${l.profileLabels.asset}</td><td>${l.profileValues.asset}</td></tr>
  </table>
</div>

<!-- EXECUTIVE SUMMARY -->
<div class="section">
  <div class="section-title">${l.execTitle}</div>
  <div style="font-size:12px;line-height:1.75;color:#334155;">${buildExecutiveSummary(profile, intel, l, lang)}</div>
</div>

<div class="page-break"></div>

<!-- BRAND SCORE -->
<div class="section">
  <div class="section-title">${l.brandScoreTitle}</div>
  <div class="score-hero">
    <div class="number">${overallScore}/100</div>
    <div class="level">${scoreLevel(overallScore, l)} \u2014 ${t(bs.benchmarkPosition, lang)}</div>
  </div>
  <h3 style="font-size:13px;font-weight:600;color:#0f172a;margin-bottom:14px;">${l.dimensionsTitle}</h3>
  ${dimensionsHTML}
</div>

<!-- DRIVERS -->
<div class="section">
  <div class="section-title">${l.driversTitle}</div>
  ${buildDrivers(intel, l, lang)}
</div>

<div class="page-break"></div>

<!-- DIGITAL PRESENCE -->
<div class="section">
  <div class="section-title">${l.digitalTitle}</div>
  ${buildDigital(intel, l, lang)}
</div>

<!-- COMMERCIAL POTENTIAL -->
<div class="section">
  <div class="section-title">${l.commercialTitle}</div>
  ${buildCommercial(intel, profile, l, lang)}
</div>

<div class="page-break"></div>

<!-- RISK ANALYSIS -->
<div class="section">
  <div class="section-title">${l.riskTitle}</div>
  ${buildRisks(intel, l, lang)}
</div>

<!-- BENCHMARKING -->
<div class="section">
  <div class="section-title">${l.benchmarkTitle}</div>
  ${buildBenchmarking(intel, l, lang)}
</div>

<!-- STRATEGIC RECOMMENDATIONS -->
<div class="section">
  <div class="section-title">${l.recsTitle}</div>
  ${buildRecommendations(intel, profile, l, lang)}
</div>

<div class="page-break"></div>

<!-- METHODOLOGY -->
<div class="section">
  <div class="section-title">${l.methodTitle}</div>
  <div class="methodology-box">${l.methodText}</div>
</div>

<!-- DISCLAIMER -->
<div class="section">
  <div class="disclaimer-box">${l.disclaimer}</div>
</div>

<!-- FOOTER -->
<div class="footer">
  <p style="font-weight:600;color:#64748b;">Your Brand Value Sports \u00b7 sports.yourbrandvalue.com \u00b7 info@yourbrandvalue.com</p>
  <p>${l.copyright}</p>
</div>
</body></html>`;
}

export function getBrandIntelligenceFileName(lang = 'en') {
  return (LABELS[lang] || LABELS.en).fileName;
}
